// DMM Affiliate Link Helper

/**
 * Get DMM affiliate ID from environment or default
 * @returns {string|null} Affiliate ID or null if not set
 */
export const getAffiliateId = () => {
  return process.env.REACT_APP_DMM_AFFILIATE_ID || null;
};

/**
 * Generate DMM affiliate URL
 * @param {string} productUrl - Original DMM product URL
 * @param {string} [customAffiliateId] - Optional custom affiliate ID
 * @returns {string} Affiliate URL or original URL if no affiliate ID
 */
export const generateAffiliateUrl = (productUrl, customAffiliateId) => {
  const affiliateId = customAffiliateId || getAffiliateId();
  
  // If no product URL, return a placeholder
  if (!productUrl) {
    return '#';
  }
  
  // If no affiliate ID, return the original URL
  if (!affiliateId) {
    console.warn('No DMM affiliate ID configured');
    return productUrl;
  }
  
  // Already an affiliate URL
  if (productUrl.includes('/affi/') || productUrl.includes('af_id=')) {
    return productUrl;
  }
  
  // Convert regular DMM URL to affiliate URL
  // Example: https://www.dmm.co.jp/digital/videoa/-/detail/=/cid=abc123/
  // Becomes: https://al.dmm.co.jp/?lurl=https%3A%2F%2Fwww.dmm.co.jp%2Fdigital%2Fvideoa%2F-%2Fdetail%2F%3D%2Fcid%3Dabc123%2F&af_id=affiliate-990&ch=toolbar&ch_id=link
  
  try {
    const encodedUrl = encodeURIComponent(productUrl);
    return `https://al.dmm.co.jp/?lurl=${encodedUrl}&af_id=${affiliateId}&ch=toolbar&ch_id=link`;
  } catch (error) {
    console.error('Error generating affiliate URL:', error);
    return productUrl;
  }
};

/**
 * Check if affiliate ID is configured
 * @returns {boolean} True if affiliate ID is set
 */
export const hasAffiliateId = () => {
  return !!getAffiliateId();
};

/**
 * Generate FANZA affiliate URL
 * @param {string} contentId - DMM content ID (e.g., 'abc123')
 * @param {string} [customAffiliateId] - Optional custom affiliate ID
 * @returns {string} FANZA affiliate URL
 */
export const generateFanzaUrl = (contentId, customAffiliateId) => {
  const affiliateId = customAffiliateId || getAffiliateId();
  
  if (!contentId) {
    return '#';
  }
  
  const baseUrl = `https://www.dmm.co.jp/digital/videoa/-/detail/=/cid=${contentId}/`;
  
  if (!affiliateId) {
    return baseUrl;
  }
  
  return generateAffiliateUrl(baseUrl, affiliateId);
};

/**
 * Extract content ID from various DMM URL formats
 * @param {string} url - DMM URL
 * @returns {string|null} Content ID or null
 */
export const extractContentId = (url) => {
  if (!url) return null;
  
  // Match patterns like cid=abc123 or /cid/abc123/
  const match = url.match(/cid[=/]([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
};

/**
 * Format affiliate link with tracking parameters
 * @param {string} url - Base URL
 * @param {object} options - Tracking options
 * @returns {string} URL with tracking parameters
 */
export const addTrackingParams = (url, options = {}) => {
  const { 
    source = 'web',
    medium = 'video_detail',
    campaign = 'bacchus'
  } = options;
  
  if (!url || url === '#') return url;
  
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign}`;
};

/**
 * Get MGS affiliate ID from environment
 * @returns {string|null} MGS affiliate ID
 */
export const getMGSAffiliateId = () => {
  return process.env.REACT_APP_MGS_AFFILIATE_ID || null;
};

/**
 * Generate MGS affiliate URL
 * @param {string} productUrl - Original MGS product URL or product code
 * @param {object} options - Optional affiliate ID and params
 * @returns {string} MGS affiliate URL
 */
export const generateMGSAffiliateUrl = (productUrl, options = {}) => {
  if (!productUrl) {
    return '#';
  }
  
  // Extract product code from URL if full URL is provided
  let productCode = productUrl;
  if (productUrl.includes('mgstage.com')) {
    const match = productUrl.match(/product_detail\/([^/]+)/);
    productCode = match ? match[1] : productUrl;
  }
  
  // Build base URL
  const baseUrl = `https://www.mgstage.com/product/product_detail/${productCode}/`;
  
  // Get affiliate ID
  const affiliateId = options.affiliateId || getMGSAffiliateId();
  
  if (affiliateId) {
    // MGS official affiliate link format
    const params = {
      agef: '1',
      utm_medium: 'mgs_affiliate',
      utm_source: 'mgs_affiliate_linktool',
      aff: affiliateId,
      utm_campaign: 'mgs_affiliate_linktool',
      utm_content: affiliateId,
      form: `mgs_asp_linktool_${affiliateId}`
    };
    
    const separator = baseUrl.includes('?') ? '&' : '?';
    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    
    return `${baseUrl}${separator}${queryString}`;
  }
  
  // No affiliate ID, return direct link with tracking
  const trackingParams = {
    source: options.source || 'bacchus',
    medium: options.medium || 'mgs_link',
    campaign: options.campaign || 'video_detail'
  };
  
  return addTrackingParams(baseUrl, trackingParams);
};

/**
 * Check if MGS affiliate is configured
 * @returns {boolean} True if MGS affiliate ID is set
 */
export const hasMGSAffiliateId = () => {
  return !!getMGSAffiliateId();
};

/**
 * Check if URL is MGS
 * @param {string} url - URL to check
 * @returns {boolean} True if MGS URL
 */
export const isMGSUrl = (url) => {
  return url && url.includes('mgstage.com');
};

/**
 * Extract MGS product code from URL
 * @param {string} url - MGS URL
 * @returns {string|null} Product code or null
 */
export const extractMGSProductCode = (url) => {
  if (!url) return null;
  
  const match = url.match(/product_detail\/([^/]+)/);
  return match ? match[1] : null;
};

/**
 * Generate affiliate URL based on source
 * @param {object} video - Video object with source and URLs
 * @param {object} options - Optional affiliate IDs and tracking params
 * @returns {string} Appropriate affiliate URL
 */
export const generateVideoAffiliateUrl = (video, options = {}) => {
  if (!video) return '#';
  
  // Check video source
  if (video.source === 'MGS' && video.mgsUrl) {
    return generateMGSAffiliateUrl(video.mgsUrl, options);
  } else if (video.dmmUrl) {
    return generateAffiliateUrl(video.dmmUrl, options.dmmAffiliateId);
  }
  
  // No valid URL found
  return '#';
};